export function toCsv(rows: Array<Record<string, unknown>>, headers?: string[]): string {
  if (rows.length === 0 && !headers?.length) return "";
  const cols = headers ?? Array.from(rows.reduce((s, r) => { Object.keys(r).forEach(k => s.add(k)); return s; }, new Set<string>()));
  const esc = (v: unknown) => {
    if (v == null) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.split('"').join('""')}"` : s;
  };
  return [cols.join(","), ...rows.map(r => cols.map(c => esc(r[c])).join(","))].join("\n");
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** Validate a URL string for required UTM params and common pitfalls. */
export type UtmIssue = { level: "error" | "warn"; field: string; message: string };

const REQUIRED_UTMS = ["utm_source", "utm_medium", "utm_campaign"] as const;
const RECOMMENDED_UTMS = ["utm_content"] as const;

export function validateUtmUrl(raw: string): { ok: boolean; issues: UtmIssue[]; parsed?: URL } {
  const issues: UtmIssue[] = [];
  if (!raw || !raw.trim()) {
    return { ok: false, issues: [{ level: "error", field: "url", message: "URL is empty." }] };
  }
  let u: URL;
  try { u = new URL(raw.trim()); }
  catch { return { ok: false, issues: [{ level: "error", field: "url", message: "Not a valid URL." }] }; }
  if (!/^https?:$/.test(u.protocol)) issues.push({ level: "error", field: "protocol", message: "Use http:// or https://." });

  for (const k of REQUIRED_UTMS) {
    const v = u.searchParams.get(k);
    if (!v) { issues.push({ level: "error", field: k, message: `Missing required ${k}.` }); continue; }
    if (v !== v.trim()) issues.push({ level: "warn", field: k, message: `${k} has leading/trailing whitespace.` });
    if (/\s/.test(v)) issues.push({ level: "warn", field: k, message: `${k} contains spaces — use underscores.` });
    if (v.toLowerCase() !== v) issues.push({ level: "warn", field: k, message: `${k} should be lowercase for consistency.` });
    if (v.length > 80) issues.push({ level: "warn", field: k, message: `${k} is unusually long (${v.length} chars).` });
    if (/[?#&]/.test(v)) issues.push({ level: "error", field: k, message: `${k} contains reserved characters.` });
  }
  for (const k of RECOMMENDED_UTMS) {
    if (!u.searchParams.get(k)) issues.push({ level: "warn", field: k, message: `Consider setting ${k} to distinguish creatives.` });
  }
  // duplicates
  const seen = new Set<string>();
  for (const [k] of u.searchParams.entries()) {
    if (seen.has(k)) issues.push({ level: "warn", field: k, message: `Duplicate parameter ${k}.` });
    seen.add(k);
  }
  return { ok: issues.every(i => i.level !== "error"), issues, parsed: u };
}